import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// ── Local-storage hydration ────────────────────────────────────────────
// Cache credits + avatar URL alongside the user object so the header can
// render Pro badge + avatar instantly on reload, instead of flashing a
// placeholder for the 200-600ms it takes the API to respond.
const CACHE_KEYS = {
  credits: 'cached_credits',
  avatar: 'cached_avatar_url',
  featureCredits: 'cached_feature_credits',
  hasActiveSubscription: 'cached_has_active_sub',
  studioTheme: 'terview:studio-theme',
};

// Colour themes offered by the studio workspaces. `dark` is the original
// studio look and applies no theme class at all. A third `gradient` theme
// (light surface, brand gradient behind it) was dropped; anyone whose
// localStorage still holds it falls through the guard below onto light.
export const STUDIO_THEMES = ['light', 'dark'];

/** Root class for a theme. Dark is deliberately empty — see STUDIO_THEMES. */
export const studioThemeClass = (t) => (t === 'light' ? 'cls-light' : '');

const EMPTY_FEATURE_CREDITS = {
  interviewAssistant: 0,
  mockInterview: 0,
  interviewQuestions: 0,
  resumeBuilder: 0,
  coverLetter: 0,
  atsCheck: 0,
  linkedinOptimizer: 0,
  portfolioBuilder: 0,
  autoApply: 0,
};

function readNumber(key) {
  const raw = localStorage.getItem(key);
  if (raw == null) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function readFeatureCredits() {
  try {
    const raw = localStorage.getItem(CACHE_KEYS.featureCredits);
    if (!raw) return EMPTY_FEATURE_CREDITS;
    return { ...EMPTY_FEATURE_CREDITS, ...JSON.parse(raw) };
  } catch { return EMPTY_FEATURE_CREDITS; }
}

// Compare two featureCredits maps by value, over the union of their keys so a
// balance the server adds later still counts. Used to keep the previous object
// when a poll returns identical numbers — see setFeatureCredits below.
function sameFeatureCredits(a, b) {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const k of keys) {
    if (a?.[k] !== b?.[k]) return false;
  }
  return true;
}

export const UserProvider = ({ children }) => {
  // Seed cached user from localStorage so navigation between routes never
  // shows a flash of "not logged in" while verify-token is in flight.
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem('userData');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [credits, setCreditsState] = useState(() => readNumber(CACHE_KEYS.credits) ?? 0);
  const [avatarUrl, setAvatarUrlState] = useState(() => localStorage.getItem(CACHE_KEYS.avatar));
  // Per-feature credit balances (e.g. interviewAssistant: 5, coverLetter: 3...)
  // and a hasActiveSubscription flag — both surfaced to feature pages so they
  // can render a "X credits left" chip without each page refetching.
  const [featureCredits, setFeatureCreditsState] = useState(readFeatureCredits);
  const [hasActiveSubscription, setHasActiveSubscriptionState] = useState(
    () => localStorage.getItem(CACHE_KEYS.hasActiveSubscription) === '1'
  );

  // Workspace colour theme, shared by every themed studio so one switch
  // governs them all and the choice follows the user between pages. Purely
  // client-side — it never round-trips to the server.
  //
  // NOTE: `dark` is the absence of a theme class, not a class of its own.
  // See the light-surface block in CoverLetterStudio.css for why.
  const [studioTheme, setStudioThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem(CACHE_KEYS.studioTheme);
      return STUDIO_THEMES.includes(saved) ? saved : 'light';
    } catch { return 'light'; }
  });
  // Stable identity — pages put this in effect deps.
  const setStudioTheme = useCallback((next) => {
    if (!STUDIO_THEMES.includes(next)) return;
    setStudioThemeState(next);
    try { localStorage.setItem(CACHE_KEYS.studioTheme, next); } catch { /* private mode */ }
  }, []);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  // These wrappers MUST keep a stable identity across renders. They used to
  // close over the current state (deps: [credits], [featureCredits], …), which
  // gave `fetchCredits` a fresh identity after every fetch and re-fired the
  // Header's `useEffect(…, [user, fetchCredits])` — an endless
  // /api/user/credits polling loop for every logged-in user. Reading the
  // previous value through the functional updater removes the dependency;
  // localStorage mirroring moved to the effects below.
  const setCredits = useCallback((value) => {
    setCreditsState(prev => (typeof value === 'function' ? value(prev) : value));
  }, []);

  const setAvatarUrl = useCallback((value) => {
    setAvatarUrlState(prev => (typeof value === 'function' ? value(prev) : value));
  }, []);

  const setFeatureCredits = useCallback((value) => {
    setFeatureCreditsState(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      const merged = { ...EMPTY_FEATURE_CREDITS, ...(next || {}) };
      return sameFeatureCredits(prev, merged) ? prev : merged;
    });
  }, []);

  const setHasActiveSubscription = useCallback((value) => {
    setHasActiveSubscriptionState(prev => !!(typeof value === 'function' ? value(prev) : value));
  }, []);

  // Mirror each value to localStorage whenever it changes, so the next reload
  // renders the credits chip / avatar with the last-known value before the API
  // responds. Effects rather than inline writes — that's what lets the setters
  // above stay dependency-free.
  useEffect(() => {
    try { localStorage.setItem(CACHE_KEYS.credits, String(credits)); } catch {}
  }, [credits]);

  useEffect(() => {
    try {
      if (avatarUrl) localStorage.setItem(CACHE_KEYS.avatar, avatarUrl);
      else localStorage.removeItem(CACHE_KEYS.avatar);
    } catch {}
  }, [avatarUrl]);

  useEffect(() => {
    try { localStorage.setItem(CACHE_KEYS.featureCredits, JSON.stringify(featureCredits)); } catch {}
  }, [featureCredits]);

  useEffect(() => {
    try { localStorage.setItem(CACHE_KEYS.hasActiveSubscription, hasActiveSubscription ? '1' : '0'); } catch {}
  }, [hasActiveSubscription]);

  const fetchCredits = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/credits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setCredits(data.credits);
        if (data.featureCredits) setFeatureCredits(data.featureCredits);
        setHasActiveSubscription(!!data.hasActiveSubscription);
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  }, [API_BASE_URL, setCredits, setFeatureCredits, setHasActiveSubscription]);

  const fetchAvatar = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.profile?.avatarUrl) {
        setAvatarUrl(data.profile.avatarUrl);
      }
    } catch (e) {
      // silently ignore
    }
  }, [API_BASE_URL, setAvatarUrl]);

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('token');
      const storedUserData = localStorage.getItem('userData');

      if (token && storedUserData) {
        try {
          // Verify token with server
          const response = await fetch(`${API_BASE_URL}/api/verify-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            console.log('Token verified, user logged in:', userData);
            // Fetch credits and avatar after verification
            fetchCredits();
            fetchAvatar();
          } else if (response.status === 401 || response.status === 403) {
            // Server explicitly rejected the token — log out.
            console.log('Token rejected (', response.status, ') — logging out');
            localStorage.removeItem('token');
            localStorage.removeItem('userData');
            setUser(null);
          } else {
            // Other non-OK responses (5xx, 502 from a slow/proxied backend)
            // are NOT auth failures. Trust the cached user for now; the next
            // authenticated request will reverify naturally.
            console.warn('verify-token returned', response.status, '— keeping cached session');
            try { setUser(JSON.parse(storedUserData)); } catch (_) { setUser(null); }
            fetchCredits();
            fetchAvatar();
          }
        } catch (error) {
          // Network error / fetch threw / timeout. Same reasoning as above:
          // do NOT clear localStorage. Trust the cached user, let the next
          // request reverify. This prevents rapid reloads or transient
          // network blips from logging the user out.
          console.error('Error verifying token (kept session):', error.message);
          try { setUser(JSON.parse(storedUserData)); } catch (_) { setUser(null); }
        }
      }
      setLoading(false);
    };

    verifyToken();
    // Safe to depend on the fetchers now that they hold a stable identity —
    // this runs once per mount. Listing them while they still changed on every
    // credits response would have re-verified the token in a tight loop.
  }, [API_BASE_URL, fetchCredits, fetchAvatar]);

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    setUser(userData);
    fetchCredits();
    fetchAvatar();
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setUser(null);
    setCredits(0);
    setAvatarUrl(null);
    setFeatureCredits(EMPTY_FEATURE_CREDITS);
    setHasActiveSubscription(false);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        credits,
        fetchCredits,
        setCredits,
        avatarUrl,
        setAvatarUrl,
        fetchAvatar,
        featureCredits,
        setFeatureCredits,
        hasActiveSubscription,
        setHasActiveSubscription,
        studioTheme,
        setStudioTheme,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};